/**
 * The campaign draft behind Create campaign (design: screens/marketing/campaignModel.ts).
 *
 * The design reads the brief with local rules and seeds a sample clinic in
 * Pune (places on a drawn map, sample copy, competitor ads, cost benchmarks).
 * Here the brief comes from the backend (/api/ads/campaign-brief) and the
 * draft starts empty; only the design's own option lists remain.
 */

/* ---------- The design's goals → Meta objectives (the same mapping as the old wizard) ---------- */

export const OBJECTIVE_META = {
  "Lead form": { id: "OUTCOME_LEADS", key: "OUTCOME_LEADS", goal: "LEAD_GENERATION", destination: undefined, copyBrief: "leads" },
  "WhatsApp chats": { id: "OUTCOME_ENGAGEMENT", key: "whatsapp", goal: "CONVERSATIONS", destination: "WHATSAPP", copyBrief: "WhatsApp chats" },
  "Phone calls": { id: "OUTCOME_ENGAGEMENT", key: "call", goal: "QUALITY_CALL", destination: "PHONE_CALL", copyBrief: "phone calls" },
  "Website visits": { id: "OUTCOME_TRAFFIC", key: "OUTCOME_TRAFFIC", goal: "LINK_CLICKS", destination: "WEBSITE", copyBrief: "website visits and bookings" },
  Awareness: { id: "OUTCOME_AWARENESS", key: "OUTCOME_AWARENESS", goal: "REACH", destination: "WEBSITE", copyBrief: "brand awareness and recall" },
};
/** The brief's objective (an objective id, or "whatsapp" / "call") → the design's goal. */
export const objectiveFromBrief = (v) => Object.keys(OBJECTIVE_META).find((k) => OBJECTIVE_META[k].key === v || OBJECTIVE_META[k].id === v) || null;

/* ---------- Calls to action ---------- */

export const CTA_OPTIONS = ["Send WhatsApp message", "Sign up", "Book now", "Call now", "Get quote", "Learn more"];
export const CTA_META = { "Send WhatsApp message": "WHATSAPP_MESSAGE", "Sign up": "SIGN_UP", "Book now": "BOOK_TRAVEL", "Call now": "CALL_NOW", "Get quote": "GET_QUOTE", "Learn more": "LEARN_MORE" };
const CTA = { "Lead form": "Sign up", "WhatsApp chats": "Send WhatsApp message", "Phone calls": "Call now", "Website visits": "Book now", Awareness: "Learn more" };
export const ctaFor = (o) => CTA[o];

/* ---------- Creative: the design's names → the backend's ids ---------- */

export const IMAGE_MODEL_ID = { Studio: "studio", "Studio Wide": "studio-wide", Everyday: "everyday", Airy: "airy", Clean: "clean", Showcase: "showcase", Classic: "classic" };
export const LAYOUT_ID = { "Split panel": "split", "Hero shot": "hero", "Dark and premium": "premium", "Claim card": "claim" };
export const PLACEMENT_SPECS = {
  "Wherever it performs": null, // absent publisher_platforms = everywhere eligible, so Meta can move budget
  "Reels and Stories": { publisher_platforms: ["facebook", "instagram"], facebook_positions: ["facebook_reels", "story"], instagram_positions: ["reels", "story"] },
  Feeds: { publisher_platforms: ["facebook", "instagram"], facebook_positions: ["feed"], instagram_positions: ["stream", "explore"] },
};

/* ---------- Olli optimising ---------- */

export const CADENCE_ID = { "Every day": "daily", "Every other day": "alternate", "Once a week": "weekly", "Only when I ask": "manual" };

/* ---------- The draft ---------- */

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const blankDraft = () => {
  const tomorrow = new Date(Date.now() + 86_400_000);
  return {
    name: "", prompt: "", objective: "Lead form", service: "",
    headline: "", primary: "", description: "", cta: ctaFor("Lead form"), url: "",
    format: "Picture", style: "Split panel", variant: 0, benefits: [], placement: "Wherever it performs",
    photo: undefined, images: [], imageModel: "Studio", imageCount: 3, imageScene: "", adPoster: true,
    videoType: "Offer explainer", videoModel: "Cinematic", videoLength: 15, voiceLanguage: "Hindi", videoScript: "", videoMedia: [], presenter: "", presenterId: "", videoUrl: "",
    // A location is a Meta city: { id, key, name, state, km, included, outsideMap }.
    locations: [], ageMin: 22, ageMax: 55, gender: "All",
    languages: [], // [{ key, name }]
    interests: [], // [{ id, name }]
    budget: 1000, start: "now", startDate: ymd(tomorrow), endDate: "",
    leadFormId: "",
    olli: { on: false, cadence: "Every day", pause: true, shift: true, raise: false, copy: false, maxChange: 20, minImpressions: 1000, approval: "ask" },
    // What the brief brought back that the canvas shows but doesn't edit.
    audience: "", tone: "", recommendations: [], copyAdditions: [], evidence: null, budgetNote: null,
    copyLanguage: "", objectiveGuessed: false,
  };
};

/** A Meta city from location search → a draft location. */
// Cities keep Meta's coordinates (item 40) so the map can draw them; a city
// without them is still targeted, it just sits off the map.
export const toCityLocation = (c, km = 25) => {
  const lat = Number(c.latitude), lng = Number(c.longitude);
  const onMap = Number.isFinite(lat) && Number.isFinite(lng) && c.latitude !== null && c.longitude !== null;
  return { id: `${c.key}-${Math.random().toString(36).slice(2, 6)}`, key: c.key, name: c.name, state: c.region || "", km, included: true, outsideMap: !onMap, ...(onMap ? { latitude: lat, longitude: lng } : {}) };
};
/** A dropped pin: a point and a radius (Meta custom location, 1–80 km). */
export const toPinLocation = (latitude, longitude, km = 5, name = "Pin") => ({ id: `pin-${Math.random().toString(36).slice(2, 8)}`, pin: true, name, state: "", km, included: true, latitude, longitude });

/** The backend brief → a draft, and the list of what it filled (for Olli's summary). */
export function draftFromBrief(d, prompt) {
  const draft = { ...blankDraft(), prompt };
  const filled = [];
  if (d.product) { draft.service = d.product; filled.push({ label: "Treatment", value: d.product, section: "goal" }); }
  // The backend always names an objective now and flags it when the sentence
  // didn't say (objective_guessed); then the clinic is asked to pick one.
  const objective = objectiveFromBrief(d.objective);
  if (objective) { draft.objective = objective; draft.cta = ctaFor(objective); }
  draft.objectiveGuessed = !objective || d.objective_guessed === true;
  filled.push({ label: "Goal", value: draft.objectiveGuessed ? "Pick one" : objective, section: "goal" });
  const t = d.targeting || {};
  const cities = (t.city_suggestions || []).slice(0, 3);
  if (cities.length) { draft.locations = cities.map((c) => toCityLocation(c)); filled.push({ label: "Places", value: draft.locations.map((l) => `${l.name} +${l.km} km`).join(", "), section: "audience" }); }
  if (t.age_min) draft.ageMin = Number(t.age_min);
  if (t.age_max) draft.ageMax = Number(t.age_max);
  if (t.age_min || t.age_max) filled.push({ label: "Ages", value: `${draft.ageMin} to ${draft.ageMax}`, section: "audience" });
  if (t.gender === "female" || t.gender === "male") { draft.gender = t.gender === "female" ? "Women" : "Men"; filled.push({ label: "Gender", value: draft.gender, section: "audience" }); }
  const budget = parseBudget(d.daily_budget_inr);
  if (budget) { draft.budget = Math.max(70, budget); filled.push({ label: "Daily budget", value: `₹${draft.budget.toLocaleString("en-IN")}`, section: "budget" }); }
  if (d.copy?.headline) draft.headline = d.copy.headline.slice(0, 40);
  if (d.copy?.primary_text) draft.primary = d.copy.primary_text;
  if (d.copy?.description) draft.description = d.copy.description.slice(0, 30);
  if (d.copy?.headline || d.copy?.primary_text) filled.push({ label: "Words", value: d.copy_language ? `Headline, text and button in ${d.copy_language}` : "Headline, text and button", section: "creative" });
  draft.copyLanguage = typeof d.copy_language === "string" && d.copy_language.trim() ? d.copy_language.trim() : "";
  if (Array.isArray(d.benefits)) draft.benefits = d.benefits.filter((b) => typeof b === "string" && b.trim()).slice(0, 3);
  if (d.image_prompt) draft.imageScene = d.image_prompt;
  if (d.landing_url) draft.url = d.landing_url;
  if (d.format?.recommended === "video") { draft.format = "Video"; draft.placement = "Reels and Stories"; }
  if (d.format?.recommended === "image") draft.adPoster = d.format.kind !== "photo";
  if (d.format?.recommended) filled.push({ label: "Format", value: d.format.recommended === "video" ? "Video" : draft.adPoster ? "Ad poster" : "Plain photo", section: "creative" });
  // Where it runs follows the creative's shape (services/adPlacements).
  if (d.placement?.recommended === "vertical") draft.placement = "Reels and Stories";
  else if (d.placement?.recommended === "automatic") draft.placement = "Wherever it performs";
  if (d.campaign_name) draft.name = d.campaign_name;
  draft.audience = d.audience || "";
  draft.tone = d.tone || "";
  draft.recommendations = Array.isArray(d.recommendations) ? d.recommendations : [];
  draft.copyAdditions = Array.isArray(d.suggested?.copy_additions) ? d.suggested.copy_additions : [];
  draft.evidence = d.evidence || null;
  draft.budgetNote = d.budget_note || null;
  return { draft, filled };
}

/**
 * A daily budget in rupees from whatever came back or was typed: 1500,
 * "1,500", "Rs 1,500", "₹1500/day", "1.5k", "2 hazaar". null without a number.
 */
export function parseBudget(v) {
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  const m = v.toLowerCase().replace(/(\d),(?=\d)/g, "$1").match(/(\d+(?:\.\d+)?)\s*(k\b|thousand|hazaa?r|lakh|lac)?/);
  if (!m) return null;
  const unit = { k: 1000, thousand: 1000, hazar: 1000, hazaar: 1000, lakh: 100000, lac: 100000 }[m[2] || ""] || 1;
  const n = Number(m[1]) * unit;
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

const OBJECTIVE_FROM_META = { LEAD_GENERATION: "Lead form", MESSAGES: "WhatsApp chats", LINK_CLICKS: "Website visits", REACH: "Awareness", BRAND_AWARENESS: "Awareness" };
const CTA_FROM_META = Object.fromEntries(Object.entries(CTA_META).map(([label, type]) => [type, label]));
const KM_PER_MILE = 1.609;
const kmOf = (radius, unit) => (radius ? Math.round(unit === "mile" ? radius * KM_PER_MILE : radius) : 0);
const ymdOf = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? ymd(d) : ""; };

/** Meta's placement keys → the design's three choices. */
function placementOf(t) {
  if (!t.publisher_platforms) return "Wherever it performs";
  const pos = [...(t.facebook_positions || []), ...(t.instagram_positions || [])];
  if (pos.length && pos.every((p) => /reels|story/.test(p))) return "Reels and Stories";
  if (pos.length && pos.every((p) => /feed|stream|explore/.test(p))) return "Feeds";
  return "Wherever it performs";
}

/**
 * An ad set's Meta targeting → the draft's places and people (the reverse of
 * buildTargeting). Cities keep Meta's names; they have no coordinates here,
 * so they sit off the map until searched again.
 */
export function draftTargeting(t) {
  if (!t || typeof t !== "object") return {};
  const geo = t.geo_locations || {};
  const city = (c, included) => ({ ...toCityLocation({ key: c.key, name: c.name || `City ${c.key}`, region: c.region || "" }, Math.max(16, kmOf(c.radius, c.distance_unit) || 17)), included });
  const pins = (geo.custom_locations || []).filter((p) => p.latitude != null && p.longitude != null)
    .map((p, i) => toPinLocation(Number(p.latitude), Number(p.longitude), Math.max(1, kmOf(p.radius, p.distance_unit) || 5), p.name || `Pin ${i + 1}`));
  const out = {
    locations: [...(geo.cities || []).map((c) => city(c, true)), ...((t.excluded_geo_locations || {}).cities || []).map((c) => city(c, false)), ...pins],
    regions: (geo.regions || []).map((r) => ({ key: r.key, name: r.name || `Region ${r.key}` })),
    countries: Array.isArray(geo.countries) ? geo.countries : [],
    placement: placementOf(t),
  };
  if (t.age_min) out.ageMin = Number(t.age_min);
  if (t.age_max) out.ageMax = Number(t.age_max);
  out.gender = Array.isArray(t.genders) && t.genders.length === 1 ? (t.genders[0] === 1 ? "Men" : "Women") : "All";
  out.languages = (t.locales || []).map((key) => ({ key, name: `Language ${key}` }));
  out.interests = (t.flexible_spec || []).flatMap((x) => x?.interests || []).filter((x) => x && x.id).map((x) => ({ id: x.id, name: x.name || String(x.id) }));
  out.customAudiences = (t.custom_audiences || []).map((a) => ({ id: a.id, name: a.name || `Audience ${a.id}` }));
  out.excludedAudiences = (t.excluded_custom_audiences || []).map((a) => ({ id: a.id, name: a.name || `Audience ${a.id}` }));
  return out;
}

/** An ad's creative (object_story_spec) → the draft's words, link, button and picture. */
export function draftCreative(c) {
  if (!c) return {};
  const spec = c.object_story_spec || {};
  const link = spec.link_data || null;
  const video = spec.video_data || null;
  const d = link || video || {};
  const ctaType = d.call_to_action?.type || c.call_to_action_type || "";
  const out = {
    headline: String((link ? d.name : d.title) || c.title || "").slice(0, 40),
    primary: String(d.message || c.body || ""),
    description: String((link ? d.description : d.link_description) || "").slice(0, 30),
    url: (link ? d.link : d.call_to_action?.value?.link) || "",
    leadFormId: d.call_to_action?.value?.lead_gen_form_id || "",
  };
  if (CTA_FROM_META[ctaType]) out.cta = CTA_FROM_META[ctaType];
  const picture = c.image_url || d.picture || d.image_url || c.thumbnail_url || null;
  if (video) { out.format = "Video"; out.videoUrl = ""; }
  else out.format = "Picture";
  // The ad's own picture, shown as it runs (a plain photo: Meta adds the headline beside it).
  if (picture) { out.images = [{ url: picture }]; out.variant = 0; out.adPoster = false; }
  return out;
}

/** The Olli optimising settings from GET …/ai-optimization → the draft's section. */
export function draftOptimisation(s) {
  if (!s) return null;
  const cadence = Object.keys(CADENCE_ID).find((k) => CADENCE_ID[k] === s.cadence) || "Every day";
  const on = (v) => v === true || v === 1 || v === "1";
  return {
    on: on(s.enabled), cadence, pause: on(s.allow_pause), shift: on(s.allow_shift_budget), raise: on(s.allow_change_budget), copy: on(s.allow_creative),
    maxChange: Number(s.max_change_pct) || 20, minImpressions: Number(s.min_impressions) || 0,
    approval: s.approval_mode === "auto_pause" ? "pause-only" : s.approval_mode === "auto" ? "auto" : "ask",
  };
}

/**
 * A campaign from GET /api/ads/campaigns/:id → a draft for Edit or Duplicate.
 * Read live from Meta it carries its first ad set (targeting, budget, dates)
 * and its first ad's creative, and those fill the canvas; the cached row
 * (Meta not reachable) has the campaign level only.
 */
export function draftFromCampaign(row, { copy = false, optimisation = null } = {}) {
  const draft = blankDraft();
  if (!row) return draft;
  draft.name = copy ? `Copy of ${row.name || "campaign"}` : row.name || "";
  const objective = objectiveFromBrief(row.objective) || OBJECTIVE_FROM_META[row.objective] || null;
  if (objective) { draft.objective = objective; draft.cta = ctaFor(objective); }
  const set = row.adset || null;
  // The cached row stores micros; Meta's own objects the currency's minor unit.
  const budget = set?.daily_budget ? Number(set.daily_budget) / 100 : row.daily_budget_micros ? Number(row.daily_budget_micros) / 1_000_000 : row.daily_budget ? Number(row.daily_budget) / 100 : null;
  if (budget) draft.budget = Math.max(70, Math.round(budget));
  if (set) {
    Object.assign(draft, draftTargeting(set.targeting));
    const starts = set.start_time ? new Date(set.start_time) : null;
    // A copy starts fresh; an edit keeps the ad set's dates.
    if (!copy && starts && starts.getTime() > Date.now()) { draft.start = "later"; draft.startDate = ymdOf(set.start_time); }
    const ends = ymdOf(set.end_time);
    if (ends && (!copy || new Date(set.end_time).getTime() > Date.now())) { draft.endDate = ends; if (copy) draft.start = "later"; }
  }
  Object.assign(draft, draftCreative(row.creative));
  if (!draft.cta) draft.cta = ctaFor(draft.objective);
  const olli = draftOptimisation(optimisation);
  if (olli) draft.olli = olli;
  return draft;
}

/** Which details a typed brief still lacks (design: missingFromBrief, without the sample place lists). */
export function missingFromBrief(prompt) {
  const p = prompt.toLowerCase();
  const hasTreatment = /implant|brace|aligner|whiten|root canal|rct|check.?up|cleaning|filling|dentist|treatment|service|surgery|therapy|consult|ivf|skin|hair|laser|physio|eye|cataract|knee|scan/.test(p);
  const hasGoal = /whats ?app|chat|lead ?form|lead|call|phone|website|site|visit|aware|reach|brand|objective|goal/.test(p);
  const hasArea = /\b(in|near|around|at)\s+[a-z]{3,}|area|location|city|pin ?code|pincode|\bkm\b/.test(p);
  const hasAudience = /\d{2}\s*(?:-|to|se)\s*\d{2}|student|college|women|ladies|female|mahila|men|male|senior|parent|elder|age|audience|everyone|family/.test(p);
  const hasBudget = /(?:₹|rs\.?\s*)[\d,]{2,7}|[\d,]{2,7}\s*(?:roz|\/day|a day|per day|daily|rupees|rs)/.test(p) || /you decide|olli decides|decide.*budget/.test(p);
  const hasFormat = /video|reel|picture|photo|image|poster|static|you decide|olli decides/.test(p);
  return [!hasTreatment && "treatment", !hasGoal && "goal", !hasArea && "area", !hasAudience && "audience", !hasBudget && "budget", !hasFormat && "format"].filter(Boolean);
}

/** Meta targeting from the draft (the same rules as the old wizard's buildTargeting). */
export function buildTargeting(d) {
  const t = { age_min: d.ageMin, age_max: d.ageMax };
  const spec = PLACEMENT_SPECS[d.placement];
  if (spec) Object.assign(t, spec);
  const city = (l) => ({ key: l.key, radius: Math.max(17, Math.min(80, l.km)), distance_unit: "kilometer" });
  const included = d.locations.filter((l) => l.included && l.key);
  const excluded = d.locations.filter((l) => !l.included && l.key);
  const pins = d.locations.filter((l) => l.pin && l.included);
  const regions = d.regions || [];
  // India only when nothing narrower was chosen: Meta reads a countries entry
  // beside custom locations as "the whole country too".
  // An ad set read back from Meta keeps its own countries (draftTargeting).
  t.geo_locations = included.length || pins.length || regions.length ? {} : { countries: (d.countries || []).length ? d.countries : ["IN"] };
  // A lookalike's delivery regions (Audiences → Use in campaign, item 57).
  if (regions.length) t.geo_locations.regions = regions.map((r) => ({ key: r.key }));
  if (included.length) t.geo_locations.cities = included.map(city);
  // Pins are also sent separately (buildPayload); kept here so the reach
  // estimate counts them.
  if (pins.length) t.geo_locations.custom_locations = pins.map((p) => ({ latitude: p.latitude, longitude: p.longitude, radius: Math.max(1, Math.min(80, p.km)), distance_unit: "kilometer" }));
  if (excluded.length) t.excluded_geo_locations = { cities: excluded.map(city) };
  if (d.gender !== "All") t.genders = [d.gender === "Men" ? 1 : 2];
  if (d.languages.length) t.locales = d.languages.map((l) => l.key);
  if (d.interests.length) t.flexible_spec = [{ interests: d.interests.map((i) => ({ id: i.id, name: i.name })) }];
  // Audiences picked on the Audiences screen (item 60). Exclusions are also
  // sent as excludedAudienceIds; kept here so the reach estimate counts them.
  if ((d.customAudiences || []).length) t.custom_audiences = d.customAudiences.map((a) => ({ id: a.id }));
  if ((d.excludedAudiences || []).length) t.excluded_custom_audiences = d.excludedAudiences.map((a) => ({ id: a.id }));
  return t;
}

/**
 * The draft with what Audiences → "Use in campaign" handed over: a Meta
 * audience to target (and a lookalike's delivery regions), the exclusions
 * picked there, or a saved audience's places and people.
 */
export function applyAudience(d, state) {
  const a = state?.audience;
  const excluded = state?.excludedAudiences || [];
  if (!a && !excluded.length) return d;
  const out = { ...d, excludedAudiences: excluded };
  if (a?.id) out.customAudiences = [{ id: a.id, name: a.name }];
  if (a?.deliverIn?.geo_locations?.regions) out.regions = a.deliverIn.geo_locations.regions.map((r) => ({ key: r.key, name: a.deliverIn.label || "Region" }));
  const t = a?.targeting;
  if (t) {
    const cities = (t.geo_locations?.cities || []).map((c) => toCityLocation({ key: c.key, name: c.name || `City ${c.key}` }, c.radius || 25));
    const pins = (t.geo_locations?.custom_locations || []).map((p, i) => toPinLocation(p.latitude, p.longitude, p.radius || 5, `Pin ${i + 1}`));
    if (cities.length || pins.length) out.locations = [...cities, ...pins];
    if (t.age_min) out.ageMin = Number(t.age_min);
    if (t.age_max) out.ageMax = Number(t.age_max);
    if (Array.isArray(t.genders) && t.genders.length === 1) out.gender = t.genders[0] === 1 ? "Men" : "Women";
    // Languages and interests go back into the draft, so buildTargeting sends
    // them on (locales, flexible_spec). locale_names ([{ key, name }]) is
    // Olli's own label list (Audiences.js); Meta only needs the keys.
    const names = new Map((Array.isArray(t.locale_names) ? t.locale_names : []).map((l) => [String(l?.key), l?.name]));
    if (Array.isArray(t.locales) && t.locales.length) out.languages = t.locales.map((key) => ({ key, name: names.get(String(key)) || `Language ${key}` }));
    const interests = (t.flexible_spec || []).flatMap((s) => s?.interests || []).filter((x) => x && x.id);
    if (interests.length) out.interests = interests.map((x) => ({ id: x.id, name: x.name || String(x.id) }));
  }
  return out;
}

/** The POST /api/ads/campaigns body (the same shape as the old wizard's buildPayload). */
export function buildPayload(d, { adAccountId, integrationId, imageUrl, callNumber }) {
  const o = OBJECTIVE_META[d.objective];
  return {
    adAccountId,
    integrationId,
    // Items 37, 38, 40, 41: the number a call ad dials, the lead form chosen on
    // the Goal step, map pins as Meta custom locations, and the treatment (for
    // cost benchmarks).
    ...(d.objective === "Phone calls" && callNumber ? { callNumber } : {}),
    ...(d.objective === "Lead form" && d.leadFormId ? { leadFormId: d.leadFormId } : {}),
    pins: d.locations.filter((l) => l.pin && l.included).map((p) => ({ latitude: p.latitude, longitude: p.longitude, radius_km: p.km, name: p.name })),
    ...((d.excludedAudiences || []).length ? { excludedAudienceIds: d.excludedAudiences.map((a) => a.id) } : {}),
    treatment: d.service || undefined,
    campaign: { name: d.name, objective: o.id },
    adset: {
      name: `${d.name} – Adset`,
      dailyBudgetRupees: Number(d.budget),
      optimizationGoal: o.goal,
      billingEvent: "IMPRESSIONS",
      targeting: buildTargeting(d),
      destinationType: o.destination || "WEBSITE",
      startTime: d.start === "later" && d.startDate ? new Date(d.startDate).toISOString() : undefined,
      endTime: d.endDate ? new Date(d.endDate).toISOString() : undefined,
    },
    creative: {
      name: `${d.name} – Creative`,
      message: d.primary || d.service,
      headline: d.headline || d.name,
      description: d.description,
      linkUrl: d.url,
      imageUrl: imageUrl || null,
      callToActionType: CTA_META[d.cta] || "LEARN_MORE",
    },
  };
}

/** The Olli optimising section → /api/ads/campaigns/:id/ai-optimization settings. */
export const optimisationSettings = (o) => ({
  enabled: o.on,
  cadence: CADENCE_ID[o.cadence] || "daily",
  allow_pause: o.pause,
  allow_shift_budget: o.shift,
  allow_change_budget: o.raise,
  allow_creative: o.copy,
  max_change_pct: o.maxChange,
  min_impressions: o.minImpressions,
  approval_mode: o.approval === "pause-only" ? "auto_pause" : o.approval,
});
